import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Crown, AlertTriangle, Check, Loader2, CalendarClock } from 'lucide-react';

const PLAN_LABELS = { free: 'Starter', starter: 'Starter', standard: 'Standard', pro: 'Pro' };
const PLAN_RANK = { starter: 0, free: 0, standard: 1, pro: 2 };

const ManageSubscriptionPage = () => {
  const [billing, setBilling] = useState('yearly');
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { user, refreshCredits } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, subRes] = await Promise.all([
          paymentsAPI.getPlans(),
          paymentsAPI.getSubscription(),
        ]);
        setPlans(plansRes.data.data.plans);
        setSubscription(subRes.data.data.subscription);
        if (subRes.data.data.subscription?.interval) {
          setBilling(subRes.data.data.subscription.interval);
        }
      } catch (err) {
        toast.error('Failed to load subscription data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const currentPlanKey = user?.plan === 'free' ? 'starter' : user?.plan;

  const handleCancel = async () => {
    setActionLoading('cancel');
    try {
      const res = await paymentsAPI.cancelSubscription();
      toast.success(res.data.data.message);
      setSubscription((prev) => prev ? { ...prev, cancelAtPeriodEnd: true } : prev);
      setShowCancelConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async () => {
    setActionLoading('resume');
    try {
      await paymentsAPI.resumeSubscription();
      toast.success('Subscription resumed!');
      setSubscription((prev) => prev ? { ...prev, cancelAtPeriodEnd: false } : prev);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resume subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangePlan = async (planKey) => {
    if (planKey === 'starter') {
      setShowCancelConfirm(true);
      return;
    }

    setActionLoading(planKey);
    try {
      const res = await paymentsAPI.changeSubscription(planKey, billing);
      toast.success(res.data.data.message);
      await refreshCredits();
      const subRes = await paymentsAPI.getSubscription();
      setSubscription(subRes.data.data.subscription);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change plan.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNewSubscription = async (planKey) => {
    if (planKey === 'starter') return;
    setActionLoading(planKey);
    try {
      const res = await paymentsAPI.createCheckoutSession(planKey, billing);
      window.location.href = res.data.data.url;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start checkout.');
      setActionLoading(null);
    }
  };

  const getButtonConfig = (planKey) => {
    const isCurrent = currentPlanKey === planKey;
    const currentRank = PLAN_RANK[currentPlanKey] || 0;
    const targetRank = PLAN_RANK[planKey] || 0;

    if (isCurrent && (!subscription || subscription.interval === billing)) {
      return { label: 'Current Plan', disabled: true, variant: 'current' };
    }

    if (isCurrent && subscription) {
      const isIntervalUpgrade = billing === 'yearly';
      return {
        label: isIntervalUpgrade ? 'Switch to Yearly' : 'Switch to Monthly',
        disabled: false,
        variant: isIntervalUpgrade ? 'upgrade' : 'downgrade',
        action: () => handleChangePlan(planKey),
      };
    }

    if (!subscription || currentPlanKey === 'starter') {
      if (planKey === 'starter') {
        return { label: 'Current Plan', disabled: true, variant: 'current' };
      }
      return {
        label: 'Subscribe',
        disabled: false,
        variant: 'upgrade',
        action: () => handleNewSubscription(planKey),
      };
    }

    if (targetRank > currentRank) {
      return {
        label: 'Upgrade',
        disabled: false,
        variant: 'upgrade',
        action: () => handleChangePlan(planKey),
      };
    }

    if (targetRank < currentRank) {
      return {
        label: planKey === 'starter' ? 'Cancel & Downgrade' : 'Downgrade',
        disabled: false,
        variant: 'downgrade',
        action: () => handleChangePlan(planKey),
      };
    }

    const isIntervalUpgrade = billing === 'yearly' && subscription?.interval === 'monthly';
    return {
      label: isIntervalUpgrade ? 'Switch to Yearly' : 'Switch to Monthly',
      disabled: false,
      variant: isIntervalUpgrade ? 'upgrade' : 'downgrade',
      action: () => handleChangePlan(planKey),
    };
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ag-black-800" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-6 md:py-10 lg:py-14 font-sans overflow-y-auto"
      style={{ scrollbarWidth: 'thin', scrollbarColor: '#c1c1c1 transparent' }}
    >
      {/* Header */}
      <div className="text-center mb-3 md:mb-5">
        <h1 className="text-2xl md:text-3xl lg:text-5xl text-gray-900 font-light italic">
          Manage your{' '}
          <span className="font-bold">subscription</span>
        </h1>
        <p className="text-gray-500 mt-2 text-sm md:text-base">
          You're on the{' '}
          <span className="text-ag-black-800 font-semibold">{PLAN_LABELS[currentPlanKey]}</span> plan
          {subscription && (
            <span className="text-gray-400"> ({subscription.interval})</span>
          )}
        </p>
      </div>

      {/* Cancellation pending banner */}
      {subscription?.cancelAtPeriodEnd && (
        <div className="w-full max-w-5xl rounded-xl border border-yellow-300 bg-yellow-50 p-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CalendarClock className="text-yellow-600" size={20} />
              <div>
                <p className="text-yellow-700 font-medium">Subscription canceling</p>
                <p className="text-sm text-yellow-600/70">
                  Your plan will downgrade to Starter on{' '}
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                    month: 'long', day: 'numeric', year: 'numeric',
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={handleResume}
              disabled={actionLoading === 'resume'}
              className="px-4 py-2 text-sm font-semibold bg-yellow-100 text-yellow-700 rounded-full border border-yellow-300 hover:bg-yellow-200 transition-colors disabled:opacity-50"
            >
              {actionLoading === 'resume' ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Resuming...
                </span>
              ) : (
                'Resume Subscription'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Billing Toggle */}
      <div className="flex items-center gap-2 ">
        <button
          onClick={() => setBilling('monthly')}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            billing === 'monthly'
              ? 'bg-primary-50 text-black border-2 border-ag-green shadow-lg shadow-ag-green/20'
              : 'bg-ag-black-800 text-white hover:bg-ag-black-700 border-2 border-ag-black-800 hover:border-ag-green/50'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling('yearly')}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            billing === 'yearly'
              ? 'bg-primary-50 text-black border-2 border-ag-green shadow-lg shadow-ag-green/20'
              : 'bg-ag-black-800 text-white hover:bg-ag-black-700 border-2 border-ag-black-800 hover:border-ag-green/50'
          }`}
        >
          Yearly
        </button>
      </div>

      <div className="mb-4 md:mb-6" />

      {/* Plans Grid */}
      <div className="w-full max-w-5xl ">
        <div
          className="flex md:grid md:grid-cols-3 gap-4 md:gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory pt-5 pb-4 md:pb-0 px-4 md:px-0"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#c1c1c1 transparent' }}
        >
          {/* Reorder: Starter, Pro (middle/highlighted), Standard */}
          {[...plans].sort((a, b) => {
            const order = { starter: 0, pro: 1, standard: 2 };
            return (order[a.key] ?? 99) - (order[b.key] ?? 99);
          }).map((plan) => {
            const pricing = plan[billing];
            const isHighlighted = plan.highlight;
            const isCurrent = currentPlanKey === plan.key;
            const btn = getButtonConfig(plan.key);
            const isLoading = actionLoading === plan.key;
            // Dark card = highlighted OR highlighted+current (Pro keeps dark bg always)
            const isDark = isHighlighted;

            return (
              <div
                key={plan.key}
                className={`relative flex flex-col rounded-2xl p-5 md:p-6 transition-all duration-200 snap-center flex-shrink-0 w-[85vw] md:w-auto ${
                  isDark
                    ? isCurrent
                      ? 'bg-ag-black-800 text-white shadow-xl scale-105 z-10 border-2 border-ag-green ring-2 ring-ag-green/20'
                      : 'bg-ag-black-800 text-white shadow-xl scale-105 z-10 border-2 border-ag-black-700'
                    : isCurrent
                      ? 'bg-white text-gray-900 shadow-xl border-2 border-ag-green ring-2 ring-ag-green/20'
                      : 'bg-white text-gray-900 shadow-md border border-gray-200'
                }`}
              >
                {/* Current Plan Badge */}
                {isCurrent && (
                  <div className="absolute -top-3.5 left-4 px-3 py-1.5 bg-ag-green text-black text-xs font-bold rounded-full flex items-center gap-1 shadow-lg z-20">
                    <Crown size={12} />
                    Current Plan
                  </div>
                )}

                {/* Plan Name */}
                <h2
                  className={`text-base md:text-lg font-bold mb-2 md:mb-3 ${
                    isCurrent || isDark ? 'text-ag-green' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h2>

                {/* Price */}
                <div className="mb-3 md:mb-4">
                  <span
                    className={`text-3xl md:text-4xl lg:text-5xl font-bold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {pricing.display}
                  </span>
                  <span
                    className={`text-sm md:text-base ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {pricing.period}
                  </span>
                </div>

                {/* Credits */}
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {plan.credits.toLocaleString()} credits included
                </p>

                {/* Action Button */}
                <button
                  onClick={btn.action}
                  disabled={btn.disabled || isLoading}
                  className={`w-full py-2.5 rounded-full text-sm font-semibold transition-colors duration-150 mb-4 md:mb-5 disabled:cursor-not-allowed ${
                    btn.variant === 'current'
                      ? isDark
                        ? 'bg-ag-green text-ag-black-800 font-bold border border-ag-green'
                        : 'bg-ag-green text-ag-black-800 font-bold border border-ag-green'
                      : btn.variant === 'upgrade'
                        ? isDark
                          ? 'bg-ag-green text-ag-black-800 hover:bg-ag-green/90 disabled:opacity-60'
                          : 'bg-ag-black-800 text-white hover:bg-ag-black-700 disabled:opacity-60'
                        : isDark
                          ? 'bg-ag-black-600 text-gray-300 hover:bg-ag-black-500 border border-ag-black-400 disabled:opacity-60'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300 disabled:opacity-60'
                  }`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Processing...
                    </span>
                  ) : btn.variant === 'current' ? (
                    <span className="flex items-center justify-center gap-2">
                      <Check size={16} />
                      {btn.label}
                    </span>
                  ) : (
                    btn.label
                  )}
                </button>

                {/* Features */}
                <div className="space-y-3 md:space-y-3.5 flex-1 min-h-0 overflow-y-auto">
                  {plan.features.map((feature, idx) => (
                    <div key={idx}>
                      <p
                        className={`font-bold text-xs md:text-sm ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {feature.label}
                      </p>
                      {feature.desc && (
                        <p
                          className={`text-xs mt-0.5 whitespace-pre-line leading-relaxed ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          {feature.desc}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cancel Subscription link — only for active paid subscribers */}
      {subscription && !subscription.cancelAtPeriodEnd && currentPlanKey !== 'starter' && (
        <button
          onClick={() => setShowCancelConfirm(true)}
          className="mt-3 md:mt-4 text-sm text-red-400 hover:text-red-500 transition-colors flex-shrink-0"
        >
          Cancel Subscription
        </button>
      )}

      {/* Back link */}
      <Link
        to="/dashboard"
        className="mt-4 md:mt-6 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors flex-shrink-0"
      >
        Back to dashboard
      </Link>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-50 rounded-full">
                <AlertTriangle className="text-red-500" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Cancel Subscription?</h3>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to cancel your <span className="text-gray-900 font-semibold">{PLAN_LABELS[currentPlanKey]}</span> subscription?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You'll keep access to your current plan features until the end of your billing period
              {subscription?.currentPeriodEnd && (
                <> on{' '}
                  <span className="text-gray-700 font-medium">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric',
                    })}
                  </span>
                </>
              )}. After that, you'll be downgraded to the Starter plan.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading === 'cancel'}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'cancel' ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Canceling...
                  </span>
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSubscriptionPage;
